import { eq, useLiveQuery } from '@tanstack/react-db';
import { createFileRoute } from '@tanstack/react-router';
import { useTable } from '@tanstack/react-table';
import { Plus } from 'lucide-react';
import { useMemo, useState } from 'react';

import { BrandDialog, CategoryDialog, TagDialog } from '@/components/catalog/taxonomy-dialogs';

import {
  DataTable,
  DataTablePagination,
  dataTableColumnHelper,
  dataTableDefaultColumn,
  dataTableFeatures,
} from '@/components/data-table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/lib/auth';
import {
  type BrandRecord,
  brandsCollection,
  type CategoryRecord,
  categoriesCollection,
  type ProductRecord,
  type ProductTagRecord,
  productsCollection,
  productTagsCollection,
} from '@/lib/collections';

/**
 * Taxonomy — how the catalog is classified: categories, brands and tags.
 *
 * Three tables behind tabs, following ctrl-saas. They are one screen because
 * they answer one question ("how is this catalog organised?") and because
 * moving a product between them is a single mental operation, but they share
 * no columns, so they stay three tables rather than one with a type column.
 *
 * Each carries a product count, which is the number that actually tells you
 * whether a classification is being used. The counts come from the products
 * collection that is already synced, so they cost nothing extra.
 */
export const Route = createFileRoute('/taxonomy/')({
  loader: async () => {
    await Promise.all([
      categoriesCollection.preload(),
      brandsCollection.preload(),
      productTagsCollection.preload(),
      productsCollection.preload(),
    ]).catch(() => undefined);
  },
  component: TaxonomyPage,
});

function TaxonomyPage() {
  const { activeAccountId, can } = useAuth();
  const accountId = activeAccountId ?? '';
  const canEdit = can('catalog.manage');

  const products = useLiveQuery(
    (q) =>
      q
        .from({ product: productsCollection })
        .where(({ product }) => eq(product.account_id, accountId)),
    [accountId],
  );

  const categories = useLiveQuery(
    (q) =>
      q
        .from({ category: categoriesCollection })
        .where(({ category }) => eq(category.account_id, accountId)),
    [accountId],
  );

  const brands = useLiveQuery(
    (q) =>
      q.from({ brand: brandsCollection }).where(({ brand }) => eq(brand.account_id, accountId)),
    [accountId],
  );

  const tags = useLiveQuery(
    (q) => q.from({ tag: productTagsCollection }).where(({ tag }) => eq(tag.account_id, accountId)),
    [accountId],
  );

  /** Product counts per category and per brand, from rows already in memory. */
  const counts = useMemo(() => {
    const byCategory = new Map<string, number>();
    const byBrand = new Map<string, number>();

    for (const product of (products.data ?? []) as ProductRecord[]) {
      if (product.category_id) {
        byCategory.set(product.category_id, (byCategory.get(product.category_id) ?? 0) + 1);
      }
      if (product.brand_id) {
        byBrand.set(product.brand_id, (byBrand.get(product.brand_id) ?? 0) + 1);
      }
    }

    return { byCategory, byBrand };
  }, [products.data]);

  const categoryRows = useMemo<CategoryRow[]>(() => {
    const all = (categories.data ?? []) as CategoryRecord[];
    const nameById = new Map(all.map((category) => [category.id, category.name]));

    return all.map((category) => ({
      id: category.id,
      name: category.name,
      slug: category.slug,
      parent: category.parent_id ? (nameById.get(category.parent_id) ?? '') : '',
      regulatory: category.regulatory_category ?? '',
      // A parent's own count excludes its children's products, which is what
      // the column says: how many products name *this* category.
      products: counts.byCategory.get(category.id) ?? 0,
      position: category.position,
      active: category.active ?? false,
    }));
  }, [categories.data, counts.byCategory]);

  const brandRows = useMemo<BrandRow[]>(
    () =>
      ((brands.data ?? []) as BrandRecord[]).map((brand) => ({
        id: brand.id,
        name: brand.name,
        slug: brand.slug,
        products: counts.byBrand.get(brand.id) ?? 0,
        active: brand.active ?? false,
      })),
    [brands.data, counts.byBrand],
  );

  const tagRows = useMemo<TagRow[]>(
    () =>
      ((tags.data ?? []) as ProductTagRecord[]).map((tag) => ({
        id: tag.id,
        name: tag.name,
        slug: tag.slug,
        kind: tag.kind ?? '',
      })),
    [tags.data],
  );

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Taxonomy</h1>
        <p className="text-muted-foreground text-sm">
          How this catalog is classified, and how much of it uses each classification.
        </p>
      </div>

      <Tabs defaultValue="categories" className="gap-4">
        <TabsList>
          <TabsTrigger value="categories">Categories ({categoryRows.length})</TabsTrigger>
          <TabsTrigger value="brands">Brands ({brandRows.length})</TabsTrigger>
          <TabsTrigger value="tags">Tags ({tagRows.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="categories" className="space-y-4">
          <CategoriesTable
            rows={categoryRows}
            records={(categories.data ?? []) as CategoryRecord[]}
            isLoading={categories.isLoading}
            canEdit={canEdit}
          />
        </TabsContent>

        <TabsContent value="brands" className="space-y-4">
          <BrandsTable
            rows={brandRows}
            records={(brands.data ?? []) as BrandRecord[]}
            isLoading={brands.isLoading}
            canEdit={canEdit}
          />
        </TabsContent>

        <TabsContent value="tags" className="space-y-4">
          <TagsTable
            rows={tagRows}
            records={(tags.data ?? []) as ProductTagRecord[]}
            isLoading={tags.isLoading}
            canEdit={canEdit}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

interface CategoryRow {
  id: string;
  name: string;
  slug: string;
  parent: string;
  regulatory: string;
  products: number;
  position: number | null;
  active: boolean;
}

const categoryColumn = dataTableColumnHelper<CategoryRow>();

const categoryColumns = categoryColumn.columns([
  categoryColumn.accessor('name', {
    header: 'Category',
    cell: ({ row }) => (
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          {/* Nesting is one level deep, so an indent marker reads better than
              a tree: it shows the relationship without implying more depth. */}
          {row.original.parent && <span className="text-muted-foreground text-xs">↳</span>}
          <span className="truncate font-medium">{row.original.name}</span>
        </div>
        <div className="text-muted-foreground truncate text-xs">{row.original.slug}</div>
      </div>
    ),
  }),
  categoryColumn.accessor('parent', {
    header: 'Parent',
    cell: ({ getValue }) => getValue() || <span className="text-muted-foreground">—</span>,
  }),
  categoryColumn.accessor('regulatory', {
    header: 'Regulatory',
    cell: ({ getValue }) =>
      getValue() ? (
        <Badge variant="outline" className="font-normal capitalize">
          {getValue()}
        </Badge>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  }),
  categoryColumn.accessor('products', {
    header: 'Products',
    meta: { className: 'text-right tabular-nums' },
    cell: ({ getValue }) => getValue().toLocaleString(),
  }),
  categoryColumn.accessor('position', {
    header: 'Order',
    meta: { className: 'text-right tabular-nums' },
    cell: ({ getValue }) => getValue() ?? '—',
  }),
  categoryColumn.accessor('active', {
    header: 'Status',
    cell: ({ getValue }) => (
      <Badge variant={getValue() ? 'default' : 'secondary'}>
        {getValue() ? 'Active' : 'Hidden'}
      </Badge>
    ),
  }),
  categoryColumn.display({
    id: 'actions',
    header: () => <span className="sr-only">Actions</span>,
    meta: { className: 'text-right' },
    cell: ({ row, table }) => (
      <Button
        variant="ghost"
        size="sm"
        onClick={() => table.options.meta?.onEdit?.(row.original.id)}
      >
        Edit
      </Button>
    ),
  }),
]);

function CategoriesTable({
  rows,
  records,
  isLoading,
  canEdit,
}: {
  rows: CategoryRow[];
  /** The raw records, which the dialog edits and uses for its parent picker. */
  records: CategoryRecord[];
  isLoading: boolean;
  canEdit: boolean;
}) {
  const [globalFilter, setGlobalFilter] = useState('');
  const [editing, setEditing] = useState<string | null | undefined>(undefined);

  const table = useTable({
    features: dataTableFeatures,
    defaultColumn: dataTableDefaultColumn,
    columns: categoryColumns,
    data: rows,
    meta: { canEdit, onEdit: (id: string) => setEditing(id) },
    state: { globalFilter },
    onGlobalFilterChange: setGlobalFilter,
    initialState: {
      pagination: { pageIndex: 0, pageSize: 25 },
      // Parents before children, then the merchandiser's order within each.
      sorting: [
        { id: 'parent', desc: false },
        { id: 'position', desc: false },
      ],
    },
  });

  return (
    <>
      <FilterRow
        value={globalFilter}
        onChange={setGlobalFilter}
        placeholder="Filter categories…"
        onNew={canEdit ? () => setEditing(null) : undefined}
        newLabel="New category"
      />
      <DataTable
        table={table}
        columnCount={categoryColumns.length}
        isLoading={isLoading}
        onRowActivate={canEdit ? (row) => setEditing(row.id) : undefined}
        emptyMessage="No categories match that filter."
      />
      {rows.length > 25 && <DataTablePagination table={table} total={rows.length} />}

      {editing !== undefined && (
        <CategoryDialog
          key={editing ?? 'new'}
          open
          onOpenChange={(next) => !next && setEditing(undefined)}
          categories={records}
          category={editing ? records.find((c) => c.id === editing) : undefined}
        />
      )}
    </>
  );
}

interface BrandRow {
  id: string;
  name: string;
  slug: string;
  products: number;
  active: boolean;
}

const brandColumn = dataTableColumnHelper<BrandRow>();

const brandColumns = brandColumn.columns([
  brandColumn.accessor('name', {
    header: 'Brand',
    cell: ({ row }) => (
      <div className="min-w-0">
        <div className="truncate font-medium">{row.original.name}</div>
        <div className="text-muted-foreground truncate text-xs">{row.original.slug}</div>
      </div>
    ),
  }),
  brandColumn.accessor('products', {
    header: 'Products',
    meta: { className: 'text-right tabular-nums' },
    cell: ({ getValue }) => getValue().toLocaleString(),
  }),
  brandColumn.accessor('active', {
    header: 'Status',
    cell: ({ getValue }) => (
      <Badge variant={getValue() ? 'default' : 'secondary'}>
        {getValue() ? 'Active' : 'Hidden'}
      </Badge>
    ),
  }),
  brandColumn.display({
    id: 'actions',
    header: () => <span className="sr-only">Actions</span>,
    meta: { className: 'text-right' },
    cell: ({ row, table }) => (
      <Button
        variant="ghost"
        size="sm"
        onClick={() => table.options.meta?.onEdit?.(row.original.id)}
      >
        Edit
      </Button>
    ),
  }),
]);

function BrandsTable({
  rows,
  records,
  isLoading,
  canEdit,
}: {
  rows: BrandRow[];
  records: BrandRecord[];
  isLoading: boolean;
  canEdit: boolean;
}) {
  const [globalFilter, setGlobalFilter] = useState('');
  const [editing, setEditing] = useState<string | null | undefined>(undefined);

  const table = useTable({
    features: dataTableFeatures,
    defaultColumn: dataTableDefaultColumn,
    columns: brandColumns,
    data: rows,
    meta: { canEdit, onEdit: (id: string) => setEditing(id) },
    state: { globalFilter },
    onGlobalFilterChange: setGlobalFilter,
    initialState: {
      pagination: { pageIndex: 0, pageSize: 25 },
      // Most-stocked first: with 157 brands, the ones carrying the catalog are
      // what you came to look at.
      sorting: [{ id: 'products', desc: true }],
    },
  });

  return (
    <>
      <FilterRow
        value={globalFilter}
        onChange={setGlobalFilter}
        placeholder="Filter brands…"
        onNew={canEdit ? () => setEditing(null) : undefined}
        newLabel="New brand"
      />
      <DataTable
        table={table}
        columnCount={brandColumns.length}
        isLoading={isLoading}
        onRowActivate={canEdit ? (row) => setEditing(row.id) : undefined}
        emptyMessage="No brands match that filter."
      />
      {rows.length > 25 && <DataTablePagination table={table} total={rows.length} />}

      {editing !== undefined && (
        <BrandDialog
          key={editing ?? 'new'}
          open
          onOpenChange={(next) => !next && setEditing(undefined)}
          brand={editing ? records.find((b) => b.id === editing) : undefined}
        />
      )}
    </>
  );
}

interface TagRow {
  id: string;
  name: string;
  slug: string;
  kind: string;
}

const tagColumn = dataTableColumnHelper<TagRow>();

const tagColumns = tagColumn.columns([
  tagColumn.accessor('name', {
    header: 'Tag',
    cell: ({ row }) => (
      <div className="min-w-0">
        <div className="truncate font-medium">{row.original.name}</div>
        <div className="text-muted-foreground truncate text-xs">{row.original.slug}</div>
      </div>
    ),
  }),
  tagColumn.accessor('kind', {
    header: 'Kind',
    cell: ({ getValue }) =>
      getValue() ? (
        <Badge variant="outline" className="font-normal capitalize">
          {getValue()}
        </Badge>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  }),
  tagColumn.display({
    id: 'actions',
    header: () => <span className="sr-only">Actions</span>,
    meta: { className: 'text-right' },
    cell: ({ row, table }) => (
      <Button
        variant="ghost"
        size="sm"
        onClick={() => table.options.meta?.onEdit?.(row.original.id)}
      >
        Edit
      </Button>
    ),
  }),
]);

function TagsTable({
  rows,
  records,
  isLoading,
  canEdit,
}: {
  rows: TagRow[];
  records: ProductTagRecord[];
  isLoading: boolean;
  canEdit: boolean;
}) {
  const [globalFilter, setGlobalFilter] = useState('');
  const [editing, setEditing] = useState<string | null | undefined>(undefined);

  const table = useTable({
    features: dataTableFeatures,
    defaultColumn: dataTableDefaultColumn,
    columns: tagColumns,
    data: rows,
    meta: { canEdit, onEdit: (id: string) => setEditing(id) },
    state: { globalFilter },
    onGlobalFilterChange: setGlobalFilter,
    initialState: {
      pagination: { pageIndex: 0, pageSize: 25 },
      sorting: [{ id: 'kind', desc: false }],
    },
  });

  return (
    <>
      <FilterRow
        value={globalFilter}
        onChange={setGlobalFilter}
        placeholder="Filter tags…"
        onNew={canEdit ? () => setEditing(null) : undefined}
        newLabel="New tag"
      />
      <DataTable
        table={table}
        columnCount={tagColumns.length}
        isLoading={isLoading}
        onRowActivate={canEdit ? (row) => setEditing(row.id) : undefined}
        emptyMessage="No tags yet."
      />
      {rows.length > 25 && <DataTablePagination table={table} total={rows.length} />}

      {editing !== undefined && (
        <TagDialog
          key={editing ?? 'new'}
          open
          onOpenChange={(next) => !next && setEditing(undefined)}
          tag={editing ? records.find((t) => t.id === editing) : undefined}
        />
      )}
    </>
  );
}

function FilterRow({
  value,
  onChange,
  placeholder,
  onNew,
  newLabel,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  /** Absent when the caller may not create records. */
  onNew?: () => void;
  newLabel?: string;
}) {
  return (
    <div className="flex justify-end gap-2">
      <Input
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="w-64"
      />

      {onNew && (
        <Button size="sm" onClick={onNew}>
          <Plus className="size-4" />
          {newLabel}
        </Button>
      )}
    </div>
  );
}
